import { useEffect, useState } from "react";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useGetCustomFieldsQuery,
  useGetCustomFieldValuesQuery,
  useSaveCustomFieldValuesMutation,
} from "../../slices/CustomFieldsSlice";
import { apiErrorText } from "./CustomFieldModals";
import { CustomFieldEntity, CustomFieldValues } from "./customFields.types";
import {
  fieldsFor,
  valueError,
  valuesChanged,
  valuesPayload,
} from "./customFields.utils";

/**
 * A client's custom field values for its Edit window: load, change, and save
 * (only when something changed). `save` returns null when saved (or nothing
 * changed), "" when a value is wrong (shown under the field), or an error message.
 */
export const useCustomFieldValues = (
  customerId: string | null | undefined,
  entityType: CustomFieldEntity,
) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: allFields } = useGetCustomFieldsQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: saved } = useGetCustomFieldValuesQuery(
    { customerId: customerId || "" },
    { skip: !customerId, refetchOnMountOrArgChange: true },
  );
  const [saveValues] = useSaveCustomFieldValuesMutation();
  const [values, setValues] = useState<CustomFieldValues>({});
  const [touched, setTouched] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (saved && !touched) setValues({ ...saved });
  }, [saved, touched]);

  const setValue = (fieldId: string, value: string) => {
    setTouched(true);
    setValues((current) => ({ ...current, [fieldId]: value }));
  };

  const save = async (): Promise<string | null> => {
    const fields = fieldsFor(allFields, entityType);
    setShowErrors(true);
    const invalid = fields
      .map((field) => valueError(field, values[field.id]))
      .find(Boolean);
    // A wrong value's message is shown under that field; stop without a second one.
    if (invalid) return "";
    if (!customerId || !valuesChanged(fields, saved, values)) return null;
    const result = await saveValues({
      customerId,
      values: valuesPayload(fields, values),
    });
    return "error" in result
      ? apiErrorText(
          result.error,
          "The custom fields couldn’t be saved. Please try again.",
        )
      : null;
  };

  return { values, setValue, save, showErrors };
};
